import { z } from "zod";

/**
 * Employee directory (WP-2.5, ADR-0021): the HR export imported by an administrator. It gives the
 * requester profile in the conversation and the business owner / second-level manager.
 */

export const DIRECTORY_IMPORT_MAX_BYTES = 10 * 1024 * 1024;
export const DIRECTORY_IMPORT_MAX_ROWS = 50_000;
/** Longest value kept from one cell; a longer one rejects the row (never silently cut). */
export const DIRECTORY_VALUE_MAX_LENGTH = 200;

/** Why a whole file is refused (400 `details.reason`). */
export const DIRECTORY_FILE_PROBLEMS = [
  "empty",
  "too_large",
  "not_text",
  "missing_columns",
  "too_many_rows",
  "no_rows",
] as const;
export type DirectoryFileProblem = (typeof DIRECTORY_FILE_PROBLEMS)[number];

/**
 * Problems with one row. The first four reject the row; `invalid_email` keeps it without the email;
 * `shared_email` means another employee has the same email, so neither is matched to a user.
 */
export const DIRECTORY_ROW_PROBLEMS = [
  "missing_employee_id",
  "missing_name",
  "duplicate_employee_id",
  "value_too_long",
  "invalid_email",
  "shared_email",
] as const;
export type DirectoryRowProblem = (typeof DIRECTORY_ROW_PROBLEMS)[number];

export const directoryImportSchema = z.object({
  id: z.uuid(),
  fileName: z.string(),
  /** Display name of the administrator; null if that user no longer exists. */
  importedBy: z.string().nullable(),
  createdAt: z.string(),
  /** Data rows in the file (header excluded, blank lines skipped). */
  rowCount: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  rejected: z.number().int(),
  encoding: z.enum(["utf-8", "windows-1252"]),
  /** Row numbers are 1-based file lines (the header is line 1). No values: they are personal data. */
  problems: z.array(z.object({ row: z.number().int(), code: z.enum(DIRECTORY_ROW_PROBLEMS) })),
  /** Header names the import does not use. */
  unknownColumns: z.array(z.string()),
});
export type DirectoryImport = z.infer<typeof directoryImportSchema>;

/** An employee as the system uses it (names in normal case). */
export const employeeSchema = z.object({
  id: z.uuid(),
  employeeNumber: z.string(),
  fullName: z.string(),
  email: z.string().nullable(),
  company: z.string().nullable(),
  country: z.string().nullable(),
  businessUnit: z.string().nullable(),
  role: z.string().nullable(),
  lineManager: z.string().nullable(),
  /** C-1 of the HR export: becomes `business_owner` on this person's requests. */
  businessOwner: z.string().nullable(),
  /** C-2 of the HR export: becomes `second_level_manager`. */
  secondLevelManager: z.string().nullable(),
  updatedAt: z.string(),
});
export type Employee = z.infer<typeof employeeSchema>;

export const employeeListQuerySchema = z.object({
  /** Matches name or email (case-insensitive substring). */
  q: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type EmployeeListQuery = z.infer<typeof employeeListQuerySchema>;

export const employeeListResponseSchema = z.object({
  items: z.array(employeeSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type EmployeeListResponse = z.infer<typeof employeeListResponseSchema>;
